import * as React from "react"
import { Card, CardContent, CardHeader, CardTitle } from "./card"
import { Badge } from "./badge"
// import { ScrollArea } from "./scroll-area"
import { CalendarDays, Pill, User, FileText } from "lucide-react"
import { ClinicalNotesService, type PrescriptionHistoryItem } from "@joinomu/shared"

interface PrescriptionHistoryProps {
  patientId: string
  className?: string
}

export function PrescriptionHistory({ patientId, className }: PrescriptionHistoryProps) {
  const [history, setHistory] = React.useState<PrescriptionHistoryItem[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    const fetchHistory = async () => {
      try {
        setLoading(true)
        setError(null)
        console.log('🔍 PRESCRIPTION HISTORY: Fetching for patient ID:', patientId)
        const historyData = await ClinicalNotesService.getPatientPrescriptionHistory(patientId)
        console.log('✅ PRESCRIPTION HISTORY: Raw data received:', historyData)
        setHistory(historyData)
      } catch (err) {
        console.error('❌ PRESCRIPTION HISTORY: Error fetching:', err)
        setError('Failed to load prescription history')
      } finally {
        setLoading(false)
      }
    }

    if (patientId) {
      fetchHistory()
    }
  }, [patientId])

  if (loading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Pill className="h-5 w-5" />
            Prescription History
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <p className="text-muted-foreground">Loading prescription history...</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Pill className="h-5 w-5" />
            Prescription History
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <p className="text-destructive">{error}</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Pill className="h-5 w-5" />
          Prescription History
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Complete medication adjustment history from clinical visits
        </p>
      </CardHeader>
      <CardContent>
        {history.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <p className="text-muted-foreground">No prescription history found</p>
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            <div className="space-y-4">
              {history.map((item, index) => (
                <div key={index} className="border rounded-lg p-4 space-y-3">
                  {/* Header with date and provider */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CalendarDays className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">
                        {new Date(item.adjustment_date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </span>
                    </div>
                    {item.provider_name && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <User className="h-3 w-3" />
                        {item.provider_name}
                      </div>
                    )}
                  </div>

                  {/* Medication info */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Pill className="h-4 w-4 text-primary" />
                      <span className="font-medium">{item.medication_name}</span>
                      {item.medication_brand && (
                        <Badge variant="outline" className="text-xs">
                          {item.medication_brand}
                        </Badge>
                      )}
                      <Badge variant="secondary" className="text-xs">
                        {item.medication_category}
                      </Badge>
                    </div>

                    {/* Changes made */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      {/* Dosage changes */}
                      {(item.previous_dosage !== item.new_dosage) && (
                        <div className="space-y-1">
                          <span className="font-medium text-muted-foreground">Dosage:</span>
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground line-through">
                              {item.previous_dosage || 'Not set'}
                            </span>
                            <span>→</span>
                            <span className="font-medium">{item.new_dosage}</span>
                          </div>
                        </div>
                      )}

                      {/* Frequency changes */}
                      {(item.previous_frequency !== item.new_frequency) && (
                        <div className="space-y-1">
                          <span className="font-medium text-muted-foreground">Frequency:</span>
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground line-through">
                              {item.previous_frequency || 'Not set'}
                            </span>
                            <span>→</span>
                            <span className="font-medium">{item.new_frequency}</span>
                          </div>
                        </div>
                      )}

                      {/* Status changes */}
                      {(item.previous_status !== item.new_status) && (
                        <div className="space-y-1">
                          <span className="font-medium text-muted-foreground">Status:</span>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">
                              {item.previous_status || 'Not set'}
                            </Badge>
                            <span>→</span>
                            <Badge 
                              variant={
                                item.new_status === 'approved' ? 'default' : 
                                item.new_status === 'denied' ? 'destructive' : 
                                item.new_status === 'discontinued' ? 'secondary' : 
                                'outline'
                              } 
                              className="text-xs"
                            >
                              {item.new_status}
                            </Badge>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Provider notes */}
                    {item.provider_notes && (
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <FileText className="h-3 w-3 text-muted-foreground" />
                          <span className="text-xs font-medium text-muted-foreground">Provider Notes:</span>
                        </div>
                        <p className="text-xs text-muted-foreground bg-muted p-2 rounded">
                          {item.provider_notes}
                        </p>
                      </div>
                    )}

                    {/* Adjustment reason */}
                    {item.adjustment_reason && (
                      <div className="space-y-1">
                        <span className="text-xs font-medium text-muted-foreground">Reason for Change:</span>
                        <p className="text-xs text-muted-foreground">{item.adjustment_reason}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}